import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { formatDecimalHours } from "../../utils/timeUtils";

export default function AdminStaffSalaryDetails() {
  const navigate = useNavigate();
  const { staffId, month, year } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(location.state || null);
  const [salaryRecord, setSalaryRecord] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMode: "Cash",
    transactionDate: new Date().toISOString().split("T")[0],
    transactionReference: "",
    notes: "",
  });

  // Determine where to go back - check if we came from /staff/salary
  const backRoute = location.state?.from || `/admin/staff/${staffId}/attendance-salary`;
  const [stats, setStats] = useState({
    presentDays: 0,
    absentDays: 0,
    totalDays: 0,
    currentMonthSalary: 0,
    totalWorkHours: 0,
    totalBreakHours: 0,
  });

  useEffect(() => {
    fetchSalaryData();
    fetchPaymentTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, month, year]);

  const fetchSalaryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Use the backend endpoint that calculates everything
      const salaryUrl = `${API_BASE_URL}/api/staff/salary/${staffId}/${month}/${year}`;

      const response = await fetch(salaryUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();

        // Set stats from backend calculation
        setStats(data.stats || { ...stats });

        // Set staff info if not already available
        if (!staffInfo && data.staffInfo) {
          const staffResponse = await fetch(`${API_BASE_URL}/api/staff/${staffId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (staffResponse.ok) {
            const staffData = await staffResponse.json();
            setStaffInfo(staffData);
          }
        }
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch salary data. Status:", response.status);
        console.error("Error response:", errorText);
        toast.error(`Failed to fetch salary data: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching salary data:", error);
      toast.error("Failed to load salary data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentTransactions = async () => {
    try {
      const token = localStorage.getItem("token");

      const paymentsResponse = await fetch(`${API_BASE_URL}/api/staff/${staffId}/salary-payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (paymentsResponse.ok) {
        const data = await paymentsResponse.json();
        const monthRecord = (data.transactions || []).find((r) => {
          const recordMonth = r.SalaryMonth || "";
          return (
            recordMonth === `${year}-${String(month).padStart(2, "0")}` ||
            (recordMonth.includes(String(month)) && recordMonth.includes(String(year)))
          );
        });

        if (monthRecord) {
          setSalaryRecord(monthRecord);
          setTransactions(monthRecord.transactions || []);
        } else {
          setSalaryRecord(null);
          setTransactions([]);
        }
      }
    } catch (error) {
      console.error("Error fetching payment transactions:", error);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      // If no salary record exists, create one first
      if (!salaryRecord) {
        toast.info("Creating salary record for this month...");

        const createSalaryResponse = await fetch(`${API_BASE_URL}/api/staff/salary/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            staffId,
            month,
            year,
            baseSalary: staffInfo?.MonthlySalary || 0,
            netPayable: netPayable,
            status: "Pending",
          }),
        });

        if (!createSalaryResponse.ok) {
          toast.error("Failed to create salary record");
          return;
        }

        const { salaryId } = await createSalaryResponse.json();

        const response = await fetch(`${API_BASE_URL}/api/staff/salary-payment/transaction`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            salaryId,
            amount: parseFloat(paymentForm.amount),
            paymentMode: paymentForm.paymentMode,
            transactionDate: paymentForm.transactionDate,
            transactionReference: paymentForm.transactionReference,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment recorded successfully");
          setOpenPaymentDialog(false);
          setPaymentForm({
            amount: "",
            paymentMode: "Cash",
            transactionDate: new Date().toISOString().split("T")[0],
            transactionReference: "",
            notes: "",
          });
          fetchPaymentTransactions();
        } else {
          const error = await response.json();
          toast.error(error.message || "Failed to record payment");
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/api/staff/salary-payment/transaction`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            salaryId: salaryRecord.SalaryId,
            amount: parseFloat(paymentForm.amount),
            paymentMode: paymentForm.paymentMode,
            transactionDate: paymentForm.transactionDate,
            transactionReference: paymentForm.transactionReference,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment recorded successfully");
          setOpenPaymentDialog(false);
          setPaymentForm({
            amount: "",
            paymentMode: "Cash",
            transactionDate: new Date().toISOString().split("T")[0],
            transactionReference: "",
            notes: "",
          });
          fetchPaymentTransactions();
        } else {
          const error = await response.json();
          toast.error(error.message || "Failed to record payment");
        }
      }
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setPaymentForm({
      amount: transaction.AmountPaid,
      paymentMode: transaction.PaymentMode,
      transactionDate: new Date(transaction.PaymentDate).toISOString().split("T")[0],
      transactionReference: transaction.TransactionReference || "",
      notes: transaction.Remarks || "",
    });
    setOpenEditDialog(true);
  };

  const handleUpdateTransaction = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE_URL}/api/staff/salary-payment/${editingTransaction.TransactionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: parseFloat(paymentForm.amount),
          paymentMode: paymentForm.paymentMode,
          transactionDate: paymentForm.transactionDate,
          transactionReference: paymentForm.transactionReference,
          notes: paymentForm.notes,
        }),
      });

      if (response.ok) {
        toast.success("Payment updated successfully");
        setOpenEditDialog(false);
        setEditingTransaction(null);
        setPaymentForm({
          amount: "",
          paymentMode: "Cash",
          transactionDate: new Date().toISOString().split("T")[0],
          transactionReference: "",
          notes: "",
        });
        fetchPaymentTransactions();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to update payment");
      }
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error("Failed to update payment");
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm("Are you sure you want to delete this payment transaction?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE_URL}/api/staff/salary-payment/${transactionId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success("Payment deleted successfully");
        fetchPaymentTransactions();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to delete payment");
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    }
  };

  const getMonthName = () => {
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  const totalPaid = transactions.reduce((sum, t) => sum + parseFloat(t.AmountPaid || 0), 0);
  const netPayable =
    staffInfo?.StaffType === "Task-Based" ? stats.taskEarnings || 0 : stats.currentMonthSalary || 0;
  const remaining = netPayable - totalPaid;

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            background: "linear-gradient(180deg,#0b0f15 0%, #121316 100%)",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 }, minHeight: "calc(100vh - 80px)", overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backRoute, { state: staffInfo })}
            sx={{ color: "#C4A574", mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#fff", fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem" } }}>
            Salary Details - {getMonthName()}
          </Typography>
        </Box>

        {/* Salary Information */}
        <Card sx={{ mb: 3, boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: "#C4A574", fontSize: { xs: "0.8rem", sm: "1.25rem", md: "1.5rem" } }}>
              Salary Information - {getMonthName()}
            </Typography>
            <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.04)" }} />
            <Grid container spacing={3} sx={{ justifyContent: 'center'}}>
              {staffInfo?.StaffType === "Task-Based" ? (
                <>
                  {/* Base Salary */}
                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          ₹{parseFloat(staffInfo?.MonthlySalary || 0).toLocaleString("en-IN")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Base Salary
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Task Earnings */}
                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          ₹{(stats.taskEarnings || 0).toLocaleString("en-IN")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Task Earnings
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Total Tasks Assigned */}
                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {stats.totalTasksAssigned || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Tasks Assigned
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Tasks Completed */}
                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {stats.tasksCompleted || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Tasks Completed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              ) : (
                <>
                  {/* Hourly Staff Cards */}
                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {stats.totalDays * 9} hrs
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Total Working Hours
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {formatDecimalHours(stats.totalWorkHours || 0)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Hours Completed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          ₹{parseFloat(staffInfo?.MonthlySalary || 0).toLocaleString("en-IN")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Base Salary
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          ₹{(stats.currentMonthSalary || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Salary Generated
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {stats.totalDays}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Working Days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {stats.presentDays}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Present Days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3}>
                    <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                          {stats.absentDays}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          Absent Days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Final Salary Card */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2d2d2d", mb: 3 }}>
          <CardContent>
            <Box sx={{ p: 2, bgcolor: "#4a4a4a", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                {staffInfo?.StaffType === "Task-Based" ? (
                  <>
                    <strong>Note:</strong> Task-based staff are paid based on completed tasks only. The base salary (₹{parseFloat(staffInfo?.MonthlySalary || 0).toLocaleString()})
                    is the target amount, but actual payment depends on task completion. Task earnings are calculated when staff members mark their assignments as complete.
                  </>
                ) : (
                  <>
                    <strong>Note:</strong> Salary will be deducted according to holidays and absent days. Sundays are excluded from working days calculation.
                  </>
                )}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Payment Summary Card */}
        <Card sx={{ mb: 3, boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#C4A574", fontSize: { xs: "0.8rem", sm: "1.25rem" } }}>
              Payment Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                      ₹{netPayable.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                      Net Payable
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                      ₹{totalPaid.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                      Total Paid
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: "#2d2d2d", boxShadow: 2 }}>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFB74D" }}>
                      ₹{remaining.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                      Remaining
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payment Transactions */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#C4A574", fontSize: { xs: "0.8rem", sm: "1.25rem" } }}>
                Payment Transactions
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenPaymentDialog(true)}
                sx={{
                  bgcolor: "#4caf50",
                  "&:hover": { bgcolor: "#45a049" },
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                }}
              >
                Add Payment
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: "#4a4a4a" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#2b2b2b" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>Mode</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#fff", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <TableRow key={transaction.TransactionId} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" } }}>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>{new Date(transaction.PaymentDate).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#A5D6A7", fontSize: { xs: "0.6rem", sm: "0.875rem" } }}>₹{parseFloat(transaction.AmountPaid).toLocaleString()}</TableCell>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>{transaction.PaymentMode}</TableCell>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>{transaction.TransactionReference || "-"}</TableCell>
                        <TableCell sx={{ color: "#e5e7eb", fontSize: { xs: "0.6rem", sm: "0.875rem" }, display: { xs: 'none', sm: 'table-cell' } }}>{transaction.Remarks || "-"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleEditTransaction(transaction)}
                              sx={{ fontSize: { xs: "0.5rem", sm: "0.75rem" } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteTransaction(transaction.TransactionId)}
                              sx={{ fontSize: { xs: "0.5rem", sm: "0.75rem" } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#9CA3AF" }}>
                        <Typography variant="body1">No payment transactions yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* ADD PAYMENT DIALOG */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#2b2b2b", color: "white", fontWeight: 600 }}>Add Payment - {getMonthName()}</DialogTitle>
        <DialogContent sx={{ bgcolor: "#4a4a4a" }}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={`Remaining: ₹${remaining.toLocaleString()}`}
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    },
                    "& .MuiFormHelperText-root": { color: "#9CA3AF" }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Mode"
                  value={paymentForm.paymentMode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                  fullWidth
                  SelectProps={{ native: true }}
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentForm.transactionDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionDate: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Transaction Reference"
                  value={paymentForm.transactionReference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                  fullWidth
                  placeholder="e.g., Cheque number, UPI transaction ID"
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this payment..."
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#4a4a4a" }}>
          <Button onClick={() => setOpenPaymentDialog(false)} sx={{ color: "#9CA3AF" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            variant="contained" 
            sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#45a049" } }}
          >
            Add Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT PAYMENT DIALOG */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#2b2b2b", color: "white", fontWeight: 600 }}>Edit Payment - {getMonthName()}</DialogTitle>
        <DialogContent sx={{ bgcolor: "#4a4a4a" }}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Mode"
                  value={paymentForm.paymentMode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                  fullWidth
                  SelectProps={{ native: true }}
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentForm.transactionDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionDate: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Transaction Reference"
                  value={paymentForm.transactionReference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                  fullWidth
                  placeholder="e.g., Cheque number, UPI transaction ID"
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this payment..."
                  InputProps={{ sx: { color: "#e5e7eb" } }}
                  sx={{ 
                    input: { color: "#e5e7eb" },
                    "& .MuiInputLabel-root": { color: "#9CA3AF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#6B7280" },
                      "&:hover fieldset": { borderColor: "#9CA3AF" },
                      "&.Mui-focused fieldset": { borderColor: "#C4A574" }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#4a4a4a" }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: "#9CA3AF" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTransaction} 
            variant="contained" 
            sx={{ bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565c0" } }}
          >
            Update Payment
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}